import { r as registerInstance, c as createEvent, h, H as Host, g as getElement } from './index-8f7fb048.js';
import { t as tokens_module } from './tokens.module-03e95cfb.js';
import { c as createAndActivateFocusTrap } from './focus-trap-d9ee9cfa.js';
import { g as getNamespacedTagFor } from './index-739b155e.js';

const marketDialogCss = ":host,::slotted(*),*{box-sizing:border-box}:host([disabled]){cursor:not-allowed !important;}:host([disabled]) *,:host([disabled]) ::slotted(*){cursor:not-allowed !important;pointer-events:none !important;}@keyframes market-popup{from{opacity:0;transform:scale(0.9, 0.9)}to{opacity:1;transform:scale(1, 1)}}@keyframes market-popdown{from{opacity:1;transform:scale(1, 1)}to{opacity:0;transform:scale(0.9, 0.9)}}@keyframes market-slideup{from{opacity:0;transform:translateY(80vh)}to{opacity:1;transform:translateY(0)}}@keyframes market-slidedown{from{opacity:1;transform:translateY(0)}to{opacity:0;transform:translateY(80vh)}}@keyframes market-slide-left-enter{from{transform:translateX(100%)}to{transform:translateX(0)}}@keyframes market-slide-left-exit{from{transform:translateX(0)}to{transform:translateX(100%)}}@keyframes market-fade-in{from{opacity:0}to{opacity:1}}@keyframes market-fade-out{from{opacity:1}to{opacity:0}}@keyframes market-input-autofill-start{from{}to{}}@keyframes market-input-autofill-cancel{from{}to{}}@keyframes market-input-search-compact-enter{from{}to{}}@keyframes market-input-search-compact-exit{from{}to{}}:host{display:flex;flex-direction:column;justify-content:stretch;overflow:hidden}:host ::slotted(main){flex:0 1 100%;overflow-y:auto;height:100%}:host ::slotted(.market-header){margin-bottom:var(--core-metrics-spacing-300);padding-top:0}:host ::slotted(.market-footer){padding-bottom:0}:host{width:100%;max-width:var(--modal-dialog-width, 100%);height:auto;padding:var(--modal-dialog-vertical-padding-size) var(--modal-dialog-horizontal-padding-size);border-radius:var(--modal-dialog-border-radius);background-color:var(--modal-dialog-background-color);animation:var(--modal-dialog-animation-enter-transition-duration)\n    market-popup\n    forwards\n    var(--modal-dialog-animation-enter-transition-easing)}:host([hidden]){animation-name:market-popdown;animation-duration:var(--modal-dialog-animation-exit-transition-duration);animation-timing-function:var(--modal-dialog-animation-exit-transition-easing)}:host([value='loadingMode']){display:flex;flex-direction:column}.market-activity-indicator{position:static;top:24px;left:40px;flex:none;color:var(--core-emphasis-fill-color)}";

const MarketDialog = class {
  constructor(hostRef) {
    registerInstance(this, hostRef);
    this.marketDialogLoaded = createEvent(this, "marketDialogLoaded", 7);
    this.marketDialogDismissed = createEvent(this, "marketDialogDismissed", 7);
    this.marketDialogDidDismiss = createEvent(this, "marketDialogDidDismiss", 7);
    this.type = 'dialog';
    this.hidden = false;
    this.dialogID = undefined;
    this.index = undefined;
    this.isLoading = false;
    this.persistent = false;
    this.trapFocus = false;
    this.animationEnterDuration = tokens_module.MODAL_DIALOG_ANIMATION_ENTER_TRANSITION_DURATION;
    this.animationExitDuration = tokens_module.MODAL_DIALOG_ANIMATION_EXIT_TRANSITION_DURATION;
  }
  /**
   * Emits the dismiss event
   * The parent context will handle actually removing elements from the DOM,
   * All the dialog needs to do it emit an event so actually closing it can be
   * some other elements problem
   */
  dismiss(options) {
    if (!this.persistent) {
      const { defaultPrevented } = this.marketDialogDismissed.emit({
        dialog: this.el,
        type: this.type,
        origin: (options === null || options === void 0 ? void 0 : options.origin) || this.el,
      });
      if (!defaultPrevented) {
        this.hidden = true;
        /**
         * Emit a marketDialogDidDismiss event when modal gets fully dismissed (after animation).
         */
        setTimeout(() => {
          this.marketDialogDidDismiss.emit({
            dialog: this.el,
            type: this.type,
            origin: this.el,
          });
        }, this.animationExitDuration);
      }
    }
    return Promise.resolve();
  }
  onTrapFocusChanged(newValue, oldValue) {
    if (newValue !== oldValue) {
      if (newValue) {
        this.activateFocusTrap();
      }
      else {
        this.deactivateFocusTrap();
      }
    }
  }
  /**
   * Activates the focus trap
   * @param {Object} [options] [focus-trap `.createFocusTrap` options](https://github.com/focus-trap/focus-trap#createoptions)
   * @param {Object} [activateOptions] [focus-trap `.activate` options](https://github.com/focus-trap/focus-trap#trapactivate)
   */
  activateFocusTrap(options, activateOptions) {
    if (this.focusTrap) {
      this.focusTrap.activate(activateOptions);
      if (!this.trapFocus) {
        this.trapFocus = true;
      }
    }
    else {
      this.focusTrap = createAndActivateFocusTrap(this.el, options, activateOptions);
    }
    return Promise.resolve();
  }
  /**
   * Deactivates the focus trap
   * @param {Object} [deactivateOptions] [focus-trap `.deactivate` options](https://github.com/focus-trap/focus-trap#trapdeactivate)
   */
  deactivateFocusTrap(deactivateOptions) {
    if (this.focusTrap) {
      this.focusTrap.deactivate(deactivateOptions);
      this.focusTrap = undefined;
    }
    return Promise.resolve();
  }
  connectedCallback() {
    this.connectedCallbackTimeout = setTimeout(() => {
      /**
       * Emit a marketDialogLoaded event when the component connects. Need this so
       * the context manager isn't rummaging around it's DOM to try and find the
       * dialog that was just appended
       */
      this.marketDialogLoaded.emit({
        dialog: this.el,
        type: this.type,
      });
      if (this.trapFocus) {
        this.activateFocusTrap();
      }
    }, this.animationEnterDuration);
  }
  disconnectedCallback() {
    this.deactivateFocusTrap();
    /**
     * Prevents error caused by race conditions during rapid mounting and
     * unmounting of component by clearing the setTimeout from connectedCallback
     * if it gets called after disconnectedCallback.
     */
    clearTimeout(this.connectedCallbackTimeout);
  }
  render() {
    const MarketActivityIndicatorTagName = getNamespacedTagFor('market-activity-indicator');
    return (h(Host, { class: "market-dialog", role: "dialog" }, this.isLoading && h(MarketActivityIndicatorTagName, null), h("slot", null)));
  }
  get el() { return getElement(this); }
  static get watchers() { return {
    "trapFocus": ["onTrapFocusChanged"]
  }; }
};
MarketDialog.style = marketDialogCss;

export { MarketDialog as market_dialog };
